import {getTestProps} from '@benchling/js-test-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {ReactNode, useCallback, useMemo, useState} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {BASE_FONT_PROXIMA, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import Tag, {TagColor, TagSize} from 'ui-library/Tag';

import DataLayoutContext, {AdditionalBlockActions} from './DataLayoutContext';
import DataLayoutSection from './DataLayoutSection';
import {DataLayout as DataLayoutType, GridBlock, LayoutEvent, LayoutPath} from './DataLayoutTypes';
import GridBlockComponent from './GridBlock';
import {getAtLayoutPath} from './utils/LayoutPathUtils';

interface DataLayoutProps {
  actionOrigin: ActionOrigin;
  additionalBlockActions?: Record<string, AdditionalBlockActions>;
  additionalTitleComponents?: ReactNode;
  clientId: string;
  isEditable?: boolean;
  onEvents?: (events: Array<LayoutEvent>) => void;
  layout: DataLayoutType;
  overflowMenuOptions?: Array<OverflowMenuOption>;
  steps?: ReadonlyArray<PipelineStep>;
}

/**
 * Component for rendering data layouts.
 *
 * Data layouts are composed of sections, which are further composed of blocks. Blocks represent
 * a single piece of UI, and layouts define how to arrange those pieces of UI, and in the future
 * what types of interactions are possible across blocks.
 *
 * See DataLayoutTypes.ts for further details on the different types of sections and blocks.
 */
export default function DataLayout({
  actionOrigin,
  additionalBlockActions,
  additionalTitleComponents,
  clientId,
  isEditable,
  onEvents,
  layout,
  overflowMenuOptions,
  steps,
}: DataLayoutProps): JSX.Element {
  const [expandedBlockPath, setExpandedBlockPath] = useState<LayoutPath | null>(null);

  const onExpandBlock = useCallback((layoutPath: LayoutPath) => {
    setExpandedBlockPath(layoutPath);
  }, []);

  const onCollapseBlock = useCallback(() => {
    setExpandedBlockPath(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      actionOrigin,
      additionalBlockActions,
      clientId,
      expandedBlockPath,
      isEditable,
      onExpandBlock,
      onCollapseBlock,
      onEvents,
      steps,
    }),
    [
      actionOrigin,
      additionalBlockActions,
      clientId,
      expandedBlockPath,
      isEditable,
      onExpandBlock,
      onCollapseBlock,
      onEvents,
      steps,
    ]
  );

  const expandedBlock = useMemo(() => {
    if (expandedBlockPath == null) {
      return null;
    }
    try {
      return getAtLayoutPath<GridBlock>(layout, expandedBlockPath);
      // eslint-disable-next-line benchlint/always-throw-from-catch -- Path may be stale after a layout resize or block removal; fall back to grid view.
    } catch {
      setExpandedBlockPath(null);
      return null;
    }
  }, [expandedBlockPath, layout]);

  return (
    <DataLayoutContext.Provider value={contextValue}>
      <div
        className={css(expandedBlock != null && styles.expandedRoot)}
        {...getTestProps({component: 'DataLayout', element: 'root'})}
      >
        <div className={css(styles.title)}>
          {layout.name}
          <Tag color={TagColor.BETA} size={TagSize.SMALL} text="BETA" />
          {additionalTitleComponents}
          {overflowMenuOptions != null && overflowMenuOptions.length > 0 && (
            <div className={css(styles.overflowMenuWrapper)}>
              <OverflowMenu menuProps={{dropLeft: true}} options={overflowMenuOptions} />
            </div>
          )}
        </div>
        {expandedBlock != null ? (
          <div className={css(styles.expandedBlock)}>
            <GridBlockComponent block={expandedBlock} layoutPath={expandedBlockPath!} hasBorder={false} />
          </div>
        ) : (
          layout.sections.map((section, index) => (
            <DataLayoutSection layoutPath={['sections', index]} section={section} key={index} />
          ))
        )}
      </div>
    </DataLayoutContext.Provider>
  );
}

const styles = StyleSheet.create({
  title: {
    ...BASE_FONT_PROXIMA,
    fontSize: 22,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    margin: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  expandedRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  expandedBlock: {
    flex: 1,
    minHeight: 0,
    margin: 10,
  },
  overflowMenuWrapper: {
    marginLeft: 'auto',
  },
});
